/**
 * Fleet bot card (ported from CC `AgentCard.tsx` onto TanStack Router
 * + the fetch fleet client). Shows status badge, name, config, last
 * seen, live health chips (from /api/v1/fleet/agents/:id/health) and
 * pause/resume quick actions; links to the /fleet/$botId detail page.
 */
import { ActionIcon, Badge, Checkbox, Group, Paper, Stack, Text, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconChevronRight, IconPlayerPause, IconPlayerPlay } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { fleetApi, type Bot } from "../../api/fleet";
import { StatusBadge } from "./StatusBadge";

export function useIssueCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      agent_id: string;
      verb: string;
      payload?: Record<string, unknown>;
      ttl_seconds?: number;
    }) => fleetApi.commands.issue(payload),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["fleet", "agent", vars.agent_id, "commands"] });
      void qc.invalidateQueries({ queryKey: ["fleet", "agents"] });
    },
  });
}

export function AgentCard({ agent, selected, onSelectedChange }: {
  agent: Bot;
  /** Bot Farm multi-select (master controls). Omit to hide the checkbox. */
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();


  // Live executor health (V3.1 `exec.health` journal). The endpoint is
  // part of the fleet API surface; tolerate its absence gracefully.
  const healthQ = useQuery({
    queryKey: ["fleet", "agent", agent.id, "health"],
    queryFn: () => fleetApi.fleet.agentHealth(agent.id),
    refetchInterval: 30_000,
    retry: false,
    enabled: agent.status === "online" || agent.status === "paused",
  });
  const health = healthQ.data;

  const goDetail = () => {
    void navigate({ to: "/fleet/$botId", params: { botId: agent.id } });
  };

  const setPaused = async (paused: boolean) => {
    try {
      const res = paused ? await fleetApi.fleet.pauseBot(agent.id) : await fleetApi.fleet.resumeBot(agent.id);
      if (!paused && res.paused) {
        const by = res.still_paused_by.map((p) => (p.scope_kind === "fleet" ? "the fleet-wide pause" : `a ${p.scope_kind} pause`)).join(", ");
        notifications.show({ color: "orange", title: "Bot pause cleared, but still paused", message: `Held by ${by || "another pause policy"} — clear it under Advanced pause policies.` });
      } else {
        notifications.show({ color: "green", message: paused ? "Bot paused." : "Bot resumed." });
      }
    } catch (err) {
      notifications.show({ color: "red", message: `${paused ? "Pause" : "Resume"} failed: ${err instanceof Error ? err.message : String(err)}` });
    }
    void qc.invalidateQueries({ queryKey: ["fleet"] });
  };

  return (
    <Paper
      p="md"
      withBorder
      style={{ cursor: "pointer", borderColor: selected ? "var(--mantine-primary-color-filled)" : undefined }}
      onClick={goDetail}
    >
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Group wrap="nowrap">
          {onSelectedChange && (
            <Checkbox
              aria-label={`Select ${agent.display_name}`}
              checked={!!selected}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onSelectedChange(e.currentTarget.checked)}
            />
          )}
          <StatusBadge status={agent.status} />
          <Text fw={600} size="sm" truncate>{agent.display_name}</Text>
        </Group>
        <ActionIcon
          variant="subtle"
          size="sm"
          onClick={(e) => { e.stopPropagation(); goDetail(); }}
        >
          <IconChevronRight size={16} />
        </ActionIcon>
      </Group>

      <Stack gap={2}>
        <Text size="xs" c="dimmed" truncate>ID: {agent.id}</Text>
        {agent.current_config_id && (
          <Text size="xs" c="dimmed" truncate>Config: {agent.current_config_id}</Text>
        )}
        <Text size="xs" c="dimmed">
          Last seen: {agent.last_seen_at ? new Date(agent.last_seen_at).toLocaleString() : "—"}
        </Text>
      </Stack>

      {health && (
        <Group gap={6} mt="xs" wrap="wrap">
          <Badge size="xs" variant="light" color={health.mode === "live" ? "red" : "blue"}>
            {health.mode}
          </Badge>
          <Badge size="xs" variant="light" color={health.user_ws_connected ? "green" : "yellow"}>
            {health.user_ws_connected ? "user-ws ●" : "user-ws ○"}
          </Badge>
          <Badge size="xs" variant="light">open: {health.open_orders}</Badge>
          {health.wallet_usdc != null && (
            <Badge size="xs" variant="light" color="teal">
              {Number(health.wallet_usdc).toFixed(2)} USDC
            </Badge>
          )}
          {(health.paused_markets ?? []).length > 0 && (
            <Badge size="xs" variant="light" color="orange">
              paused: {(health.paused_markets ?? []).join(", ")}
            </Badge>
          )}
        </Group>
      )}

      <Group mt="sm" gap="xs" onClick={(e) => e.stopPropagation()}>
        <Tooltip label="Pause">
          <ActionIcon
            size="sm" variant="light" color="yellow"
            disabled={agent.status !== "online"}
            onClick={() => void setPaused(true)}
          >
            <IconPlayerPause size={14} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Resume">
          <ActionIcon
            size="sm" variant="light" color="green"
            disabled={agent.status !== "paused"}
            onClick={() => void setPaused(false)}
          >
            <IconPlayerPlay size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Paper>
  );
}
